import * as R from 'ramda'
import { useEffect, useRef, useState } from 'react'
import { Message } from '@weblite/ts-action/message'
import { scrollToMessageByIid } from '$logic/opened-room'
import { getLastPinInViewPort, getTopViewportMessageIid } from '$logic/pin'
import { usePrevious } from '$common/react-hooks/use-previous'

const DELAY_BEFORE_UPDATING_ANIMATING_STATUS = 100

const findPinBeforeIid = (iids: number[], iid: number) => {
  if (!iids.length) return 0

  if (!iid || iids.length === 1) return iids.length - 1

  for (let idx = iids.length - 1; idx >= 0; idx -= 1) {
    if (iids[idx] <= iid) return idx
  }
  return 0
}

export function useClickOnPin(messages: Message[]) {
  const [isAnimating, setIsAnimating] = useState(false)
  const [index, setIndex] = useState(0)

  const endAnimationTimeoutRef = useRef<NodeJS.Timeout>()

  const iids = R.pluck('iid', messages)

  useEffect(() => {
    if (iids.length) setIndex(iids.length - 1)
  }, [iids.length])

  async function onClick() {
    if (endAnimationTimeoutRef.current) {
      clearTimeout(endAnimationTimeoutRef.current)
      endAnimationTimeoutRef.current = undefined
    }
    setIsAnimating(true)
    await scrollToMessageByIid(
      iids[index],
      {
        highlight: true,
        smooth: 'smooth',
        block: 'center',
      },
      undefined,
      () => {
        endAnimationTimeoutRef.current = setTimeout(() => {
          setIsAnimating(false)
        }, DELAY_BEFORE_UPDATING_ANIMATING_STATUS)
      },
    )
    setIndex(currentIndex =>
      currentIndex ? currentIndex - 1 : iids.length - 1,
    )
  }

  const prevMessages = usePrevious(messages)

  const getLast = () => {
    if (isAnimating) return
    const lastPinInPage = getLastPinInViewPort()
    if (!messages) return
    const pinIndex = messages.findIndex(
      message => message.iid === lastPinInPage,
    )

    setIndex(
      pinIndex === -1
        ? findPinBeforeIid(iids, getTopViewportMessageIid())
        : pinIndex,
    )
  }

  useEffect(() => {
    if (!prevMessages && messages) getLast()
  }, [messages])

  return { onClick, currentIndex: index, getLast, setIsAnimating, isAnimating }
}
